// @flow
import ConceptDetailModel from "./ConceptDetailModel";
import ConceptTypeDetailModel from "./ConceptTypeDetailModel";
import { getSetting } from "../../constants/Settings";

import type { ModularUIResponse } from "../../modularui";
import type { ModularUIModel } from "../types";
import type LinkModel from "../links/LinkModel";
import type ConceptLinkModel from "./ConceptLinkModel";
import type ErrorResponse from "../error/ErrorResponse";

/**
 * @deprecated use the ConceptDetailModel with concept type BusinessScenarios#BusinessScenario
 */
class BusinessScenarioModel extends ConceptDetailModel {
  _scenarioSteps: Array<ConceptDetailModel>;

  /**
   */
  get type(): string {
    return "BusinessScenario";
  }

  /**
   */
  static get modelName(): string {
    return "BusinessScenario";
  }

  /**
   */
  static isApplicableModel(data: ModularUIResponse): boolean {
    return BusinessScenarioModel.isOfConceptType(
      data?.data?._links?.concepttype?.href,
      ["SCENARIO"],
    );
  }

  /**
   */
  static isOfConceptType(
    conceptTypeHref: ?string,
    settingNames: Array<"STEP" | "PERSONA" | "SCENARIO" | "SYSTEM">,
  ): boolean {
    if (!conceptTypeHref) {
      return false;
    }

    // Check if settingNames is an array
    if (!Array.isArray(settingNames)) {
      throw new TypeError("settingNames must be an array");
    }

    const pathToKmt = "/Library/KMTs/Business scenarios.bixml";

    const conceptTypeSettings = {
      STEP: getSetting("BUSINESS_SCENARIO_STEP_CONCEPT_TYPE", [
        `${pathToKmt}/ScenarioStep`,
      ]),
      PERSONA: getSetting("BUSINESS_SCENARIO_PERSONA_CONCEPT_TYPE", [
        `${pathToKmt}/Persona`,
      ]),
      SYSTEM: getSetting("BUSINESS_SCENARIO_SYSTEM_CONCEPT_TYPE", [
        `${pathToKmt}/System`,
      ]),
      SCENARIO: getSetting("BUSINESS_SCENARIO_CONCEPT_TYPE", [
        `${pathToKmt}/BusinessScenario`,
      ]),
    };

    const decodedConceptTypeHref = decodeURIComponent(conceptTypeHref);

    return settingNames.some(
      (settingName) =>
        Array.isArray(conceptTypeSettings[settingName]) &&
        conceptTypeSettings[settingName].some((conceptTypeSetting) =>
          decodedConceptTypeHref.endsWith(conceptTypeSetting),
        ),
    );
  }

  /**
   */
  getInitialChildModelLinks(): Array<LinkModel> {
    const childModelLinks = super.getInitialChildModelLinks();

    const scenarioStepLinks = this.relationsCollection.all
      .filter(
        (relation) =>
          relation.direction === "outgoing" &&
          BusinessScenarioModel.isOfConceptType(
            relation.concept.concepttypeHref?.path,
            ["STEP"],
          ),
      )
      .map((relation) => relation.concept.asLinkModel());

    if (scenarioStepLinks.length > 0) {
      childModelLinks.push(...scenarioStepLinks);
    }

    return childModelLinks;
  }

  /**
   */
  setChildModels(models: Array<ModularUIModel>, errors: Array<ErrorResponse>) {
    // $FlowExpectedError[incompatible-type]
    const conceptTypeModel: ?ConceptTypeDetailModel = models.find(
      (model) => model instanceof ConceptTypeDetailModel,
    );

    if (conceptTypeModel) {
      this.conceptType = conceptTypeModel;
    }

    this.relationsCollection.setChildModels(models, errors);

    const scenarioSteps = [];
    for (let i = 0; i < models.length; i++) {
      const model = models[i];

      if (
        model instanceof ConceptDetailModel &&
        BusinessScenarioModel.isOfConceptType(
          model.conceptType?.selfhref.path,
          ["STEP"],
        )
      ) {
        scenarioSteps.push(model);
      }
    }

    this.scenarioSteps = scenarioSteps;
  }

  /**
   */
  set scenarioSteps(models: Array<ConceptDetailModel>) {
    this._scenarioSteps = models;
  }

  /**
   */
  get scenarioSteps(): Array<ConceptDetailModel> {
    return this._scenarioSteps;
  }

  /**
   */
  get actors(): Array<ConceptLinkModel> {
    const actors = [];

    this.scenarioSteps.forEach((scenarioStep) => {
      const relationsToActor = scenarioStep.relationsCollection.find(
        (relation) =>
          relation.direction === "outgoing" &&
          BusinessScenarioModel.isOfConceptType(
            relation.concept.concepttypeHref?.path,
            ["PERSONA", "SYSTEM"],
          ),
      );

      const newRelationToActor =
        relationsToActor &&
        !actors.some((actor) => actor.key === relationsToActor.concept.key);

      if (relationsToActor && newRelationToActor) {
        actors.push(relationsToActor.concept);
      }
    });

    return actors;
  }
}

export default BusinessScenarioModel;
